import TrendsService from '../../api/trends';
import filteringHelper from 'src/services/filteringHelper';
import Vue from 'vue';

const state = {
  trends: {
    publication: null,
    patent: null,
  },
  trendsTreemapOptions: {},
  messages: { publication: null, patent: null },
  apiFailed: { publication: false, patent: false },
  shouldRecomputeTrends: { publication: false, patent: false },
  treemapImage: null,
  statusPollingInterval: {
    publication: null,
    patent: null,
  },
};

const getters = {
  trends: (state) => {
    return state.trends;
  },
  message: (state) => {
    return state.messages;
  },
  apiFailed: (state) => {
    return state.apiFailed;
  },
  trendsTreemapOptions: (state) => {
    return state.trendsTreemapOptions;
  },
  shouldRecomputeTrends: (state) => {
    return state.shouldRecomputeTrends;
  },
};

const actions = {
  async fetchTrends({ commit, rootGetters, dispatch, state }, payload) {
    // If source is included in payload, use it, otherwise use the current active data source
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    // Clear store before fetching again
    dispatch('clearStoreBySource', { source });
    // Set message to loading
    commit('setMessage', { source, data: { task_status: 'LOADING' } });

    // If we don't already have treemapOptions, fetch them. Check source so we don't make a request for both sources as options are the same.
    if (
      source === 'publication' &&
      Object.keys(state.trendsTreemapOptions).length < 1
    ) {
      dispatch('fetchTrendsTreemapOptions');
    }

    const { scan_id, query, version_id } = rootGetters['scans/getScanDetails'];
    const scanType = rootGetters['scans/scanType'];

    // Set query_builder to query by default
    let query_builder = query;

    // Pass current filters for the source to the Trends service. If none, pass an empty object
    const filters =
      rootGetters['filters/currentFiltering'][source] &&
      Object.keys(rootGetters['filters/currentFiltering'][source]).length
        ? Object.assign({}, rootGetters['filters/currentFiltering'][source])
        : {};

    if (filters) {
      // Process filters based on scan type
      if (scanType === 'boolean') {
        // Check if search_within_results is included in filters object
        if (filters.search_within_results) {
          // Call helper function to add search within results to query
          query_builder = filteringHelper.filterResultsText(
            query,
            filters.search_within_results
          );
        }
      } else {
        // Check if positive or negative filters are included in filters object
        if (filters.positive || filters.negative) {
          // Call helper function to add positive and negative filters to query
          query_builder = filteringHelper.filterSemantic(query_builder, {
            positive: filters.positive,
            negative: filters.negative,
          });
        }
      }
    }

    const _payload = {
      source: source,
      filters: filters,
      query: query_builder,
      scan_id,
      scan_type: scanType,
      version_id,
    };

    const _trends = await TrendsService.fetchTrends(_payload);
    if (_trends && _trends.data && _trends.trends_computed) {
      // Set trends in store
      commit('setTrends', { source, data: _trends.data });
      // Clear message
      commit('setMessage', { source, data: null });
    } else {
      // dispatch call to compute trends
      dispatch('computeTrends', { source });
    }

    return;
  },

  async fetchTrendsTreemapOptions({ commit }) {
    const _trendsTreemapOptions =
      await TrendsService.fetchTrendsTreemapOptions();
    if (_trendsTreemapOptions) {
      // Commit mutation to store response from Trends Service
      commit('setTrendsTreemapOptions', _trendsTreemapOptions);
    }
  },

  async computeTrends({ rootGetters, dispatch, commit }, payload) {
    // Get source from payload if included, otherwise use the current active data source
    const source =
      payload && payload.source
        ? payload.source
        : rootGetters['app/activeDataSource'];

    //check local storage if scan has open task id
    let task_dict = JSON.parse(localStorage.getItem('task_dict') || '{}');

    const { version_id, scan_id, query } = rootGetters['scans/getScanDetails'];
    const scanType = rootGetters['scans/scanType'];

    if (task_dict && task_dict[source] && task_dict[source][version_id]) {
      // Check task status
      dispatch('checkStatus', {
        source,
        task_id: task_dict[source][version_id],
      });
      return;
    }

    // Set query_builder to query by default
    let query_builder = query;
    // Pass current filters for the source to the Trends service. If none, pass an empty object
    const filters =
      rootGetters['filters/currentFiltering'][source] &&
      Object.keys(rootGetters['filters/currentFiltering'][source]).length
        ? Object.assign({}, rootGetters['filters/currentFiltering'][source])
        : {};

    if (filters) {
      // Process filters based on scan type
      if (scanType === 'boolean') {
        // Check if search_within_results is included in filters object
        if (filters.search_within_results) {
          // Call helper function to add search within results to query
          query_builder = filteringHelper.filterResultsText(
            query,
            filters.search_within_results
          );
        }
      } else {
        // Semantic scan
        // Check if positive or negative filters are included in filters object
        if (filters.positive || filters.negative) {
          // Call helper function to add positive and negative filters to query
          query_builder = filteringHelper.filterSemantic(query_builder, {
            positive: filters.positive,
            negative: filters.negative,
          });
        }
      }
    }

    // else update workflow compute trends
    const _payload = {
      source: source,
      filters: filters,
      query: query_builder,
      max_documents: 50000,
      scan_id,
      scan_type: scanType,
      version_id,
    };
    const _trends = await TrendsService.computeTrends(_payload);

    if (_trends && _trends.task_id) {
      let task_dict = JSON.parse(localStorage.getItem('task_dict') || '{}');
      // Check if task_dict has an entry for the source. If not, create it.
      if (!task_dict[source]) {
        task_dict[source] = {};
      }
      // Set local storage task ids for the source and scan version
      task_dict[source][version_id] = _trends.task_id;

      // Set local storage
      localStorage.setItem('task_dict', JSON.stringify(task_dict));
      dispatch('checkStatus', { source, task_id: _trends.task_id });
    } else {
      // Set API failed
      commit('setApiFailed', { source, data: true });
    }
  },

  async checkStatus({ commit, rootGetters }, payload) {
    const { source, task_id } = payload;

    // from local storage
    const _payload = {
      task_id,
    };

    let task_dict = JSON.parse(localStorage.getItem('task_dict') || '{}');
    // "36b566b9-8a7d-4a96-8eec-05aa4ff8f2b4"
    const { version_id } = rootGetters['scans/getScanDetails'];

    // set timer for every 1 second to check status
    const status_polling = setInterval(async () => {
      const _task = await TrendsService.getTrendComputeStatus(_payload);
      if (_task) {
        if (_task && _task.task_result) {
          if (_task.task_result.errorAt) {
            // The trends service failed
            commit('setApiFailed', { source, data: true });
            delete task_dict[source][version_id];
            localStorage.setItem('task_dict', JSON.stringify(task_dict));
          } else {
            // we will have trends
            commit('setTrends', { source, data: _task.task_result.data });
            // dump task from local storage
            delete task_dict[source][version_id];
            localStorage.setItem('task_dict', JSON.stringify(task_dict));
          }
          commit('setMessage', { source, data: null });
          commit('clearStatusPollingInterval', { source });
        } else if (_task.task_status) {
          commit('setMessage', { source, data: _task });
        }
      } else {
        commit('setApiFailed', { source, data: true });
        commit('clearStatusPollingInterval', { source });
      }
    }, 1000);
    // Commit mutation to store the interval id
    commit('setStatusPollingInterval', {
      source,
      data: status_polling,
    });
  },

  // Used to delete all trends associated with a scan_id -OR- to delete a specific set of Trends that match the query & filters for a scan_id
  async deleteTrends({ commit, rootGetters }, payload) {
    let _payload = {
      scan_id: payload.scan_id,
    };
    // If query and filters are included in payload, we want to delete a specific trends entry, not all trends entries associated with a scan. Modify payload accordingly, for what the Trends service expects
    if (payload.query && payload.filters) {
      // Add properties to payload
      _payload.query = payload.query;
      _payload.filters = payload.filters;
      _payload.source =
        payload && payload.source
          ? payload.source
          : rootGetters['app/activeDataSource'];
    }

    // Call Trends service, pass in payload
    const response = await TrendsService.deleteTrends(_payload);

    if (response) {
    } else {
      console.warn('Error deleting trends.');
    }
  },

  shouldRecomputeTrends({ commit }, payload) {
    commit('setShouldRecomputeTrends', payload);
  },

  async clearStoreBySource({ commit }, payload) {
    const { source } = payload;
    commit('setTrends', { source, data: null });
    commit('setApiFailed', { source, data: false });
    commit('setMessage', { source, data: null });
    commit('clearStatusPollingInterval', { source });
    commit('setShouldRecomputeTrends', { source, data: false });
  },

  async clearStore({ commit }) {
    commit('setTrends');
    commit('setApiFailed');
    commit('setMessage');
    commit('clearStatusPollingInterval');
    commit('setShouldRecomputeTrends');
  },
};

// mutations
const mutations = {
  updateTrends(state, payload) {
    state.trends = payload;
  },
  setTrends(state, payload) {
    if (!payload) {
      state.trends = {
        publication: null,
        patent: null,
      };
    } else {
      Vue.set(state.trends, payload.source, payload.data);
    }
  },
  setApiFailed(state, payload) {
    if (!payload) {
      state.apiFailed = {
        publication: false,
        patent: false,
      };
    } else {
      Vue.set(state.apiFailed, payload.source, payload.data);
    }
  },
  setMessage(state, payload) {
    if (!payload) {
      state.messages = {
        publication: null,
        patent: null,
      };
    } else {
      Vue.set(state.messages, payload.source, payload.data);
    }
  },
  setTrendsTreemapOptions(state, payload) {
    state.trendsTreemapOptions = payload;
  },
  setShouldRecomputeTrends(state, payload) {
    if (!payload) {
      state.shouldRecomputeTrends = {
        publication: false,
        patent: false,
      };
    } else {
      Vue.set(state.shouldRecomputeTrends, payload.source, payload.data);
    }
  },
  treemapImage(state, payload) {
    state.treemapImage = payload;
  },
  setStatusPollingInterval(state, payload) {
    Vue.set(state.statusPollingInterval, payload.source, payload.data);
  },
  clearStatusPollingInterval(state, payload) {
    if (!payload) {
      // Clear all intervals
      for (const source in state.statusPollingInterval) {
        clearInterval(state.statusPollingInterval[source]);
        Vue.set(state.statusPollingInterval, source, null);
      }
    } else {
      // Clear specific interval
      clearInterval(state.statusPollingInterval[payload.source]);
      // Reset state
      Vue.set(state.statusPollingInterval, payload.source, null);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
